import { useState, useMemo } from 'react'
import { updateParty } from '../../utils/partyGame'
import { IconPlus } from '../icons'

/**
 * App-wide team builder used by any party mode that needs teams.
 *
 * Roles:
 *   - 'director' — tap a player to select, tap a team card to place. Tap a
 *      player already on a team to send them back to the unassigned pool.
 *      Tap the "+ Add Team" button to add another numbered team card.
 *      Tap "Lock It In" once every player is on a team and ≥2 teams exist.
 *   - 'player'   — waiting screen with live update of "you're on Team N"
 *   - 'host'     — read-only mirror of the director's current configuration
 *
 * Firestore shape: party.teams = [{ id, name, playerIds: [uid, ...] }, ...]
 *
 * Note: this uses tap-to-move instead of true drag-and-drop so it works
 * reliably on both phone (director's device) and TV (read-only mirror).
 * The UX is functionally identical — tap a player to pick them up,
 * tap a destination to drop them.
 */
export default function PartyTeamBuilder({ party, currentUserId, role = 'player' }) {
  const players = party?.players ?? {}
  const teams   = Array.isArray(party?.teams) ? party.teams : []

  // Compute the "unassigned pool" — players not present in any team
  const assignedIds = useMemo(() => {
    const s = new Set()
    teams.forEach((t) => (t.playerIds || []).forEach((uid) => s.add(uid)))
    return s
  }, [teams])

  const unassigned = useMemo(
    () => Object.entries(players).filter(([uid]) => !assignedIds.has(uid)),
    [players, assignedIds],
  )

  // Director-only: which player is "picked up" (selected for next placement)
  const [selectedUid, setSelectedUid] = useState(null)

  // ── Director-only mutators ────────────────────────────────────────────────
  const writeTeams = (nextTeams) => updateParty(party.id, { teams: nextTeams }).catch(() => {})

  const handleAddTeam = () => {
    if (role !== 'director') return
    const nextIdx = teams.length
    const newTeam = {
      id: `team_${nextIdx + 1}`,
      name: `Team ${nextIdx + 1}`,
      playerIds: [],
    }
    writeTeams([...teams, newTeam])
  }

  const handlePlayerTap = (uid) => {
    if (role !== 'director') return
    setSelectedUid((cur) => (cur === uid ? null : uid))
  }

  const handleTeamCardTap = (teamId) => {
    if (role !== 'director' || !selectedUid) return
    // Remove from any team, then add to the target team's playerIds (end)
    const nextTeams = teams.map((t) => ({
      ...t,
      playerIds: (t.playerIds || []).filter((u) => u !== selectedUid),
    }))
    const targetIdx = nextTeams.findIndex((t) => t.id === teamId)
    if (targetIdx >= 0) {
      nextTeams[targetIdx] = {
        ...nextTeams[targetIdx],
        playerIds: [...nextTeams[targetIdx].playerIds, selectedUid],
      }
    }
    setSelectedUid(null)
    writeTeams(nextTeams)
  }

  const handleSendBackToPool = (uid) => {
    if (role !== 'director') return
    const nextTeams = teams.map((t) => ({
      ...t,
      playerIds: (t.playerIds || []).filter((u) => u !== uid),
    }))
    setSelectedUid(null)
    writeTeams(nextTeams)
  }

  // Validation for confirm button
  const allAssigned = unassigned.length === 0 && Object.keys(players).length > 0
  const enoughTeams = teams.length >= 2
  const canLock     = allAssigned && enoughTeams

  const handleLockIn = async () => {
    if (!canLock) return
    await updateParty(party.id, { status: 'team_locked' })
  }

  // ── PLAYER waiting view ───────────────────────────────────────────────────
  if (role === 'player') {
    const myTeam = teams.find((t) => (t.playerIds || []).includes(currentUserId))
    return (
      <div className="party-player-screen">
        <p className="party-player-screen__headline" style={{ fontSize: '1.4rem' }}>
          Director is building teams...
        </p>
        {myTeam ? (
          <p style={{ color: 'var(--accent-gold)', fontWeight: 700, marginTop: 8 }}>
            You're on {myTeam.name}
          </p>
        ) : (
          <p style={{ color: 'var(--text-muted)', marginTop: 8 }}>
            Hang tight — they'll get to you.
          </p>
        )}
        <p className="party-player-screen__pulse" style={{ marginTop: 16 }}>Stand by</p>
      </div>
    )
  }

  // ── DIRECTOR + HOST shared builder UI ─────────────────────────────────────
  const isReadOnly = role === 'host'

  return (
    <div className={`team-builder${isReadOnly ? ' team-builder--readonly' : ''}`}>
      <div className="team-builder__header">
        <h2 className="team-builder__title">
          {isReadOnly ? 'Director is building teams' : 'Build the Teams'}
        </h2>
        {!isReadOnly && (
          <p className="team-builder__instructions">
            {selectedUid
              ? 'Tap a team card to drop the selected player'
              : 'Tap a player, then tap a team card.'}
          </p>
        )}
      </div>

      {/* Unassigned pool */}
      <div className="team-builder__pool">
        <p className="team-builder__pool-label">
          Unassigned ({unassigned.length})
        </p>
        <div className="team-builder__pool-grid">
          {unassigned.length === 0 && (
            <p className="team-builder__empty">All players placed!</p>
          )}
          {unassigned.map(([uid, p]) => (
            <PlayerChip
              key={uid}
              player={p}
              selected={selectedUid === uid}
              onClick={isReadOnly ? undefined : () => handlePlayerTap(uid)}
            />
          ))}
        </div>
      </div>

      {/* Team cards */}
      <div className="team-builder__teams">
        {teams.map((team) => (
          <button
            type="button"
            key={team.id}
            className={`team-builder__team-card${selectedUid && !isReadOnly ? ' team-builder__team-card--droppable' : ''}`}
            onClick={isReadOnly ? undefined : () => handleTeamCardTap(team.id)}
            disabled={isReadOnly}
          >
            <p className="team-builder__team-name">{team.name}</p>
            <div className="team-builder__team-players">
              {(team.playerIds || []).length === 0 && (
                <p className="team-builder__team-empty">Drop a player here</p>
              )}
              {(team.playerIds || []).map((uid) => {
                const p = players[uid]
                if (!p) return null
                return (
                  <PlayerChip
                    key={uid}
                    player={p}
                    onClick={isReadOnly ? undefined : (e) => {
                      e?.stopPropagation?.()
                      handleSendBackToPool(uid)
                    }}
                    compact
                  />
                )
              })}
            </div>
          </button>
        ))}
      </div>

      {/* Director actions */}
      {!isReadOnly && (
        <div className="team-builder__actions">
          <button
            type="button"
            className="btn-ghost team-builder__add-team-btn"
            onClick={handleAddTeam}
          >
            <IconPlus size={16} aria-hidden="true" />
            <span>Add Team</span>
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleLockIn}
            disabled={!canLock}
            title={
              !enoughTeams
                ? 'Need at least 2 teams'
                : !allAssigned
                ? `${unassigned.length} player(s) still unassigned`
                : 'Confirm teams'
            }
          >
            Lock It In
          </button>
        </div>
      )}
    </div>
  )
}

function PlayerChip({ player, selected, onClick, compact }) {
  const cls = [
    'team-builder__chip',
    compact ? 'team-builder__chip--compact' : '',
    selected ? 'team-builder__chip--selected' : '',
    onClick ? 'team-builder__chip--clickable' : '',
  ].join(' ').trim()
  const Tag = onClick ? 'button' : 'div'
  const initial = (player.username || '?').charAt(0).toUpperCase()
  return (
    <Tag className={cls} onClick={onClick} type={onClick ? 'button' : undefined}>
      <div className="team-builder__chip-avatar">
        {player.avatar
          ? <img src={player.avatar} alt={player.username} />
          : <span className="party-avatar__initial">{initial}</span>}
      </div>
      <span className="team-builder__chip-name">{player.username || 'Guest'}</span>
    </Tag>
  )
}
